import { Component, OnInit, ViewChild, ChangeDetectorRef, ChangeDetectionStrategy } from '@angular/core';
import { MatDialog } from '@angular/material/dialog';
import { MatTable, MatTableDataSource } from '@angular/material/table';
import { PostnummerDto } from 'src/app/api/models/OK/Egis/Service/Models/DTO/postnummer-dto';
import { PostnummerServicePartnerEntity } from 'src/app/api/models/OK/Egis/Service/Models/Entities/postnummer-service-partner-entity';
import { PartnerPostnummerService, PartnerService } from 'src/app/api/services';
import { EGIS_SERVICE_CALLER } from 'src/app/constants';
import { AuthService } from 'src/app/services/auth.service';
import { ErrorHandlerService } from 'src/app/services/error-handler.service';
import { LoaderService } from 'src/app/services/loader.service';
import { PartnerPostnummerEditDialogComponent } from '../partner-postnummer-edit-dialog/partner-postnummer-edit-dialog.component';
import { UntypedFormBuilder, UntypedFormGroup, Validators } from '@angular/forms';
import { PartnerServiceProduktEnum } from 'src/app/api/models/OK/Egis/Service/Models/Entities/Enums/partner-service-produkt-enum';
import { ActivatedRoute } from '@angular/router';
import { ExcelDownloadDialogModel } from 'src/app/components/shared/excel/excel-download-dialog/excel-download-dialog.model';
import { ExcelDocumentTypeEnum } from 'src/app/api/models/OK/Egis/Service/Models/DTO/Enums/excel-document-type-enum';
import { ExcelDocumentHandlerTypeEnum } from 'src/app/api/models/OK/Egis/Service/Models/DTO/Enums/excel-document-handler-type-enum';
import { ExcelDownloadDialogComponent } from 'src/app/components/shared/excel/excel-download-dialog/excel-download-dialog.component';

@Component({
  selector: 'app-postnummer-list',
  templateUrl: './postnummer-list.component.html',
  styleUrls: ['./postnummer-list.component.scss'],
  changeDetection: ChangeDetectionStrategy.Eager,
  standalone: false,
})
export class PostnummerListComponent implements OnInit {
  postnumre: PostnummerDto[] = [];
  partnerPostnummer: PostnummerServicePartnerEntity[] = [];
  @ViewChild(MatTable) table: MatTable<any>;
  displayedColumns: string[] = ['postnummer', 'partner', 'senestePartner', 'dato'];
  form: UntypedFormGroup;
  dataSource: any;
  fra: number;
  til: number;
  routedProdukt?: string;
  currentDate: Date = new Date();

  shownNumbers: number = 50;

  constructor(
    private formBuilder: UntypedFormBuilder,
    private dialog: MatDialog,
    private service: PartnerPostnummerService,
    private auth: AuthService,
    private errorHandler: ErrorHandlerService,
    private loader: LoaderService,
    private route: ActivatedRoute,
  ) {
    this.dataSource = new MatTableDataSource(this.partnerPostnummer);
  }

  async ngOnInit(): Promise<void> {
    this.route.queryParams.subscribe((params) => {
      const postnummer = params['postnummer'];
      const produkt = params['produkt'];
      if (postnummer.length != 0) {
        this.fra = postnummer;
        this.til = postnummer;
        this.routedProdukt = produkt;
        this.openDialog(true);
      }
    });
    this.fra = 1000;
    this.til = this.fra + 50;
    this.form = this.formBuilder.group({
      searchFra: [this.fra],
      searchTil: [this.til],
    });

    await this.fetchPostnumreAsync(this.fra, this.til);
    this.groupPostnummere();
  }

  async fetchPostnumreAsync(fra, til) {
    this.loader.show();
    await this.service
      .getPostnummerServicePartnere({
        fromPostnummer: fra,
        toPostnummer: til,
        caller: EGIS_SERVICE_CALLER,
      })
      .toPromise()
      .then((response) => {
        this.partnerPostnummer = response;
        this.groupPostnummere();
      })
      .catch((error) => {
        this.errorHandler.HandleError(error);
      })
      .finally(() => {
        this.loader.hide();
      });
  }

  get canEdit() {
    return this.auth.isAdministrator;
  }

  isValidPartner(validTo: string) {
    var validDate = new Date();
    if (validTo != null) {
      validDate = new Date(validTo);
    }
    if (this.currentDate < validDate) {
      return true;
    }
    return false;
  }

  latestPartner(row: any) {
    var latestPartner = null;
    row.partnere.forEach((partner) => {
      if (!this.isValidPartner(partner.validTo) && latestPartner == null) {
        latestPartner = partner;
      } else if (!this.isValidPartner(partner.validTo) && partner.validTo >= latestPartner.validTo) {
        latestPartner = partner;
      }
    });

    if (latestPartner == null) {
      return [];
    }
    return row.partnere.filter((partner) => partner.validTo == latestPartner.validTo);
  }

  getPartnerAllowFrieTilkald(partner: any) {
    return partner.allowFrieTilkald == true ? 'Tillader frie tilkald' : 'Ingen frie tilkald';
  }

  produktIkon(produkt: any) {
    return produkt.toLowerCase();
  }

  async onSearch() {
    this.fra = this.form.get('searchFra').value;
    this.til = this.form.get('searchTil').value;
    await this.fetchPostnumreAsync(this.form.get('searchFra').value, this.form.get('searchTil').value);
  }

  async remove() {
    this.openDialog(false);
  }

  async edit() {
    this.openDialog(true);
  }

  async openDialog(isEdit: boolean) {
    let dialogRef = this.dialog.open(PartnerPostnummerEditDialogComponent, {
      width: '600px',
      maxWidth: '95vw',
      data: {
        fra: this.fra,
        til: this.til,
        produkt: this.routedProdukt,
        isEdit: isEdit,
      },
    });

    await dialogRef.afterClosed().subscribe(() => {
      this.fetchPostnumreAsync(this.form.get('searchFra').value, this.form.get('searchTil').value);
    });
  }

  get aktivePostnummerPartner() {
    const aktive = [];

    this.partnerPostnummer.forEach((element) => {
      aktive.push(element);
    });
    return aktive.sort((a, b) => (a.postnummer.postnummer < b.postnummer.postnummer ? -1 : 1));
  }

  groupPostnummere() {
    const data: {
      postnummerId: number;
      postnummer: number;
      partner: string;
      produkt: PartnerServiceProduktEnum;
      validTo: string;
      allowFrieTilkald: boolean;
    }[] = this.aktivePostnummerPartner.map((postnummerPartner) => {
      return {
        postnummerId: postnummerPartner.postnummerId,
        postnummer: postnummerPartner.postnummer.postnummer,
        partner: postnummerPartner.partnerId != null ? postnummerPartner.partner.name : 'Område og produkt køres ikke',
        produkt: postnummerPartner.partnerServiceProduktId,
        validTo: postnummerPartner.validTo,
        allowFrieTilkald: postnummerPartner.allowFrieTilkald,
      };
    });

    this.dataSource = this.groupByPostnummer(data);
  }

  private groupByPostnummer(
    data: {
      postnummerId: number;
      postnummer: number;
      partner: string;
      produkt: PartnerServiceProduktEnum;
      validTo: string;
      allowFrieTilkald: boolean;
    }[],
  ): {
    postnummerId: number;
    postnummer: number;
    partnere: { name: string; produkt: PartnerServiceProduktEnum; validTo: string; allowFrieTilkald: boolean }[];
  }[] {
    const groupedByPostnummer: {
      postnummerId: number;
      postnummer: number;
      partnere: { name: string; produkt: PartnerServiceProduktEnum; validTo: string; allowFrieTilkald: boolean }[];
    }[] = [];

    data.forEach((value, index, array) => {
      const foundElement = groupedByPostnummer.find((element) => element.postnummer === value.postnummer);

      if (foundElement) {
        groupedByPostnummer[groupedByPostnummer.indexOf(foundElement)].partnere.push({
          name: value.partner,
          produkt: value.produkt,
          validTo: value.validTo,
          allowFrieTilkald: value.allowFrieTilkald,
        });
        groupedByPostnummer[groupedByPostnummer.indexOf(foundElement)].partnere.sort((a, b) => (a.produkt < b.produkt ? -1 : 1));
      } else {
        groupedByPostnummer.push({
          postnummerId: value.postnummerId,
          postnummer: value.postnummer,
          partnere: [
            {
              name: value.partner,
              produkt: value.produkt,
              validTo: value.validTo,
              allowFrieTilkald: value.allowFrieTilkald,
            },
          ],
        });
      }
    });
    return groupedByPostnummer;
  }

  download() {
    const data: ExcelDownloadDialogModel = {
      documentType: ExcelDocumentTypeEnum.PartnerPostcode,
      handlerType: ExcelDocumentHandlerTypeEnum.Default,
      filter: '',
    };
    this.dialog.open(ExcelDownloadDialogComponent, {
      data: data,
    });
  }
}
